/**
 * Die einzige Stelle, an der das Frontend das Netz anfasst (IS-32).
 *
 * Alles, was hier gebündelt ist, wäre sonst in jeder Komponente noch einmal zu
 * bedenken: Cookies mitschicken, CSRF-Token setzen, Fehlerkörper auspacken,
 * zwischen „Fehler von der API" und „API nicht erreichbar" unterscheiden.
 *
 * **Kein API-Schlüssel, kein Token im `localStorage`** (ADR 0001). Die Sitzung
 * reist im HttpOnly-Cookie, das der Browser von selbst mitschickt — JavaScript
 * kommt nicht daran, und eingeschleustes JavaScript deshalb auch nicht.
 */
import type { components } from './schema'

export type Fehlerkoerper = components['schemas']['ErrorBody']

/** Namen aus `app/api/deps.py` — Vertragsbestandteil, nicht frei wählbar. */
const CSRF_COOKIE = 'is_csrf'
const CSRF_HEADER = 'X-CSRF-Token'

/** Methoden, die der Server ohne CSRF-Prüfung durchlässt (`SICHERE_METHODEN`). */
const SICHERE_METHODEN = new Set(['GET', 'HEAD', 'OPTIONS'])

/**
 * Ein Fehler, den die API bewusst geschickt hat.
 *
 * Trägt `key` statt eines Satzes: den Text erzeugt das Frontend
 * (`app/api/errors.py`). Und die `request_id` — ohne sie ist eine Störung im
 * Serverlog nicht wiederzufinden, und der Nutzer hat nichts zu nennen.
 */
export class ApiFehler extends Error {
  readonly status: number
  readonly code: string
  readonly key: string
  readonly requestId: string
  readonly params?: Record<string, string>
  readonly details?: components['schemas']['ErrorDetail'][]

  constructor(status: number, koerper: Fehlerkoerper) {
    super(`${status} ${koerper.code}`)
    this.name = 'ApiFehler'
    this.status = status
    this.code = koerper.code
    this.key = koerper.key
    this.requestId = koerper.request_id
    this.params = koerper.params ?? undefined
    this.details = koerper.details ?? undefined
  }

  /** Die Sitzung fehlt oder ist abgelaufen — zurück zur Anmeldung. */
  get istAbgemeldet(): boolean {
    return this.status === 401
  }

  /** Angemeldet, aber ohne das nötige Recht (`app/core/rollen.py`). */
  get istVerboten(): boolean {
    return this.status === 403
  }
}

/**
 * Die API war nicht zu erreichen — Netz weg, Server aus, Zeitgrenze gerissen.
 *
 * **Bewusst ein eigener Typ.** „Nicht nachgesehen" ist etwas anderes als „nichts
 * gefunden", und die Oberfläche darf das nie verwechseln: ein leerer Bildschirm,
 * weil das Netz weg war, sieht sonst aus wie „alles in Ordnung".
 */
export class NichtErreichbar extends Error {
  constructor(readonly ursache: unknown) {
    super('API nicht erreichbar')
    this.name = 'NichtErreichbar'
  }
}

/**
 * Das CSRF-Token aus dem lesbaren Cookie.
 *
 * Es liegt absichtlich NICHT in HttpOnly (`app/api/auth.py`): das Frontend muss
 * es lesen, um es im Header mitzuschicken. Es ist kein Geheimnis, sondern ein
 * Herkunftsnachweis — geprüft wird gegen das Token der Sitzung, nicht gegen das
 * Cookie, sonst hülfe es gegen einen Angreifer mit Cookie-Zugriff nicht.
 */
export function csrfToken(): string {
  for (const teil of document.cookie.split(';')) {
    const [name, ...rest] = teil.trim().split('=')
    if (name === CSRF_COOKIE) return decodeURIComponent(rest.join('='))
  }
  return ''
}

async function ruf<T>(methode: string, pfad: string, koerper?: unknown): Promise<T> {
  const kopf: Record<string, string> = { Accept: 'application/json' }
  if (koerper !== undefined) kopf['Content-Type'] = 'application/json'
  if (!SICHERE_METHODEN.has(methode)) {
    const token = csrfToken()
    if (token) kopf[CSRF_HEADER] = token
  }

  let antwort: Response
  try {
    antwort = await fetch(pfad, {
      method: methode,
      headers: kopf,
      // Gleiche Herkunft: die Oberfläche wird vom selben Prozess ausgeliefert
      // wie die API (app/api/oberflaeche.py). Im Entwicklungsbetrieb sorgt der
      // Vite-Proxy dafür, dass das auch dort gilt.
      credentials: 'same-origin',
      body: koerper === undefined ? undefined : JSON.stringify(koerper),
    })
  } catch (ursache) {
    throw new NichtErreichbar(ursache)
  }

  if (!antwort.ok) {
    throw new ApiFehler(antwort.status, await fehlerkoerper(antwort))
  }

  if (antwort.status === 204 || antwort.headers.get('content-length') === '0') {
    return undefined as T
  }
  return (await antwort.json()) as T
}

/**
 * Den Fehlerkörper auspacken — und zur Not einen bauen.
 *
 * Ein Proxy oder der Entwicklungsserver kann mit HTML antworten, ohne die API je
 * erreicht zu haben. Dann gibt es keinen `error`-Block, und der Aufrufer soll
 * trotzdem einen `ApiFehler` mit brauchbarem Code bekommen statt einer Ausnahme
 * beim Auspacken.
 */
async function fehlerkoerper(antwort: Response): Promise<Fehlerkoerper> {
  try {
    const inhalt = (await antwort.json()) as { error?: Fehlerkoerper }
    if (inhalt.error?.code) return inhalt.error
  } catch {
    // fällt unten durch
  }
  return {
    code: 'unbekannt',
    key: 'api.error.unbekannt',
    request_id: '-',
  }
}

export const holen = <T>(pfad: string) => ruf<T>('GET', pfad)
export const senden = <T>(pfad: string, koerper?: unknown) => ruf<T>('POST', pfad, koerper)
export const ersetzen = <T>(pfad: string, koerper?: unknown) => ruf<T>('PUT', pfad, koerper)
export const loeschen = <T>(pfad: string) => ruf<T>('DELETE', pfad)
